/**
 * 我的订单页面 - 中式典雅风格（移动端优化）
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Tag,
  Button,
  Typography,
  Spin,
  Space,
  Image,
  Card,
  Grid,
  Empty,
  Pagination,
  message,
  List,
  Row,
  Col,
} from 'antd';
import {
  EyeOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  WalletOutlined,
  RightOutlined,
} from '@ant-design/icons';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';
import { useAuth } from '../../contexts/AuthContext';
import { orderApi } from '../../api/index';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

// 订单状态映射
const STATUS_MAP = {
  pending:    { color: '#D4B483', label: '待付款' },
  paid:       { color: COLORS.bambooGreen, label: '已付款' },
  processing: { color: '#6B8E9F', label: '处理中' },
  shipped:    { color: '#7D6B9F', label: '已发货' },
  delivered:  { color: COLORS.bambooGreen, label: '已送达' },
  completed:  { color: COLORS.bambooGreen, label: '已完成' },
  cancelled:  { color: COLORS.cinnabar, label: '已取消' },
};

// 财务状态映射
const FINANCIAL_STATUS_MAP = {
  pending: { color: '#D4B483', label: '待支付' },
  paid: { color: COLORS.bambooGreen, label: '已支付' },
  refunded: { color: COLORS.cinnabar, label: '已退款' },
  partially_refunded: { color: '#B95C5C', label: '部分退款' },
};

export default function MyOrdersPage() {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    fetchOrders(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchOrders = async (currentPage) => {
    setLoading(true);
    try {
      const data = await orderApi.myOrders({ page: currentPage, pageSize });
      setOrders(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setOrders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount) => `$${(amount || 0).toFixed(2)}`;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getStatusConfig = (status) => STATUS_MAP[status] || { color: COLORS.lightInk, label: status };

  const getFinancialStatusConfig = (status) => FINANCIAL_STATUS_MAP[status] || { color: COLORS.lightInk, label: status || '未知' };

  // 桌面端表格列
  const columns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
      render: (orderNo) => (
        <Text style={{ color: COLORS.ink, fontFamily: 'monospace' }}>
          {orderNo}
        </Text>
      ),
    },
    {
      title: '下单日期',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (createdAt) => (
        <Space>
          <CalendarOutlined style={{ color: COLORS.lightInk }} />
          <Text style={{ color: COLORS.lightInk }}>{formatDate(createdAt)}</Text>
        </Space>
      ),
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = getStatusConfig(status);
        return (
          <Tag
            style={{
              background: `${config.color}15`,
              borderColor: config.color,
              color: config.color,
            }}
          >
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: '支付状态',
      dataIndex: 'financialStatus',
      key: 'financialStatus',
      width: 100,
      render: (financialStatus, record) => {
        const config = getFinancialStatusConfig(financialStatus);
        const hasRefund = (record.refundedAmount || 0) > 0;
        return (
          <Space size={4}>
            <Tag
              style={{
                background: `${config.color}15`,
                borderColor: config.color,
                color: config.color,
                fontSize: 12,
              }}
            >
              {config.label}
            </Tag>
            {hasRefund && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                -{formatPrice(record.refundedAmount)}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: '总金额',
      dataIndex: 'total',
      key: 'total',
      width: 90,
      render: (total, record) => {
        const refundedAmount = record.refundedAmount || 0;
        const displayAmount = refundedAmount > 0 ? total - refundedAmount : total;
        return (
          <Space direction="vertical" size={0}>
            <Text strong style={{ color: COLORS.cinnabar }}>
              {formatPrice(displayAmount)}
            </Text>
            {refundedAmount > 0 && (
              <Text type="secondary" style={{ fontSize: 11, color: '#999' }}>
                原{formatPrice(total)}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/orders/${record.orderNo}`)}
          style={{ color: COLORS.teaBrown }}
        >
          查看详情
        </Button>
      ),
    },
  ];

  // 移动端订单卡片
  const renderMobileOrderCard = (order) => {
    const config = getStatusConfig(order.status);
    const financialConfig = getFinancialStatusConfig(order.financialStatus);
    const refundedAmount = order.refundedAmount || 0;
    const hasRefund = refundedAmount > 0;
    const displayAmount = hasRefund ? order.total - refundedAmount : order.total;

    return (
      <Card
        key={order.id}
        style={{
          borderRadius: 12,
          border: 'none',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          marginBottom: 16,
          cursor: 'pointer',
        }}
        onClick={() => navigate(`/orders/${order.orderNo}`)}
        bodyStyle={{ padding: 16 }}
      >
        {/* 订单号和状态 */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Text strong style={{ color: COLORS.ink, fontSize: 14, fontFamily: 'monospace' }}>
              {order.orderNo}
            </Text>
          </Col>
          <Col>
            <Space size={4}>
              <Tag
                style={{
                  background: `${config.color}15`,
                  borderColor: config.color,
                  color: config.color,
                  fontSize: 12,
                }}
              >
                {config.label}
              </Tag>
              {hasRefund && (
                <Tag
                  style={{
                    background: `${financialConfig.color}15`,
                    borderColor: financialConfig.color,
                    color: financialConfig.color,
                    fontSize: 11,
                  }}
                >
                  {financialConfig.label}
                </Tag>
              )}
            </Space>
          </Col>
        </Row>

        {/* 下单日期 */}
        <Row style={{ marginBottom: 12 }}>
          <Col>
            <Space size={4}>
              <CalendarOutlined style={{ color: COLORS.lightInk, fontSize: 12 }} />
              <Text style={{ color: COLORS.lightInk, fontSize: 13 }}>
                {formatDate(order.createdAt)}
              </Text>
            </Space>
          </Col>
        </Row>

        {/* 退款金额提示 */}
        {hasRefund && (
          <Row style={{ marginBottom: 8 }}>
            <Col>
              <Text type="secondary" style={{ fontSize: 12, color: COLORS.cinnabar }}>
                已退款：{formatPrice(refundedAmount)}
              </Text>
            </Col>
          </Row>
        )}

        {/* 订单金额和查看详情 */}
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={0}>
              <Space>
                <WalletOutlined style={{ color: COLORS.cinnabar }} />
                <Text strong style={{ color: COLORS.cinnabar, fontSize: 18 }}>
                  {formatPrice(displayAmount)}
                </Text>
              </Space>
              {hasRefund && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  原价 {formatPrice(order.total)}
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Space size={4}>
              <Text style={{ color: COLORS.teaBrown, fontSize: 13 }}>查看详情</Text>
              <RightOutlined style={{ color: COLORS.teaBrown, fontSize: 12 }} />
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  if (loading && orders.length === 0) {
    return (
      <StorefrontLayout>
        <div style={{ textAlign: 'center', padding: '120px 0' }}>
          <Spin size="large" />
        </div>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '16px 12px' : '32px 24px' }}>
        <Title
          level={2}
          style={{
            color: COLORS.ink,
            marginBottom: 8,
            fontSize: isMobile ? 20 : 24,
            letterSpacing: '0.15em',
          }}
        >
          我的订单
        </Title>
        <Text
          style={{
            color: COLORS.lightInk,
            fontSize: 13,
            display: 'block',
            marginBottom: isMobile ? 16 : 24,
          }}
        >
          仅显示最近2个月的订单
        </Text>

        {orders.length === 0 ? (
          <Card
            style={{
              borderRadius: 12,
              border: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            }}
          >
            <Empty
              description={
                <Text style={{ color: COLORS.lightInk }}>暂无订单记录</Text>
              }
            >
              <Button
                type="primary"
                onClick={() => navigate('/products')}
                style={{
                  borderRadius: 8,
                  background: COLORS.teaBrown,
                  borderColor: COLORS.teaBrown,
                }}
              >
                去购物
              </Button>
            </Empty>
          </Card>
        ) : (
          <>
            {/* 移动端：卡片列表 */}
            {isMobile ? (
              <div>
                {orders.map((order) => renderMobileOrderCard(order))}
              </div>
            ) : (
              /* 桌面端：表格 */
              <Card
                style={{
                  borderRadius: 12,
                  border: 'none',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                }}
              >
                <Table
                  columns={columns}
                  dataSource={orders}
                  rowKey="id"
                  pagination={false}
                  loading={loading}
                  scroll={{ x: 700 }}
                />
              </Card>
            )}

            {total > pageSize && (
              <Pagination
                current={page}
                total={total}
                pageSize={pageSize}
                onChange={(p) => setPage(p)}
                showSizeChanger={false}
                style={{ textAlign: 'center', marginTop: isMobile ? 16 : 24 }}
                simple={isMobile}
              />
            )}
          </>
        )}
      </div>
    </StorefrontLayout>
  );
}